/**
 * Probe 실패 사유 → 사용자 문구.
 *
 * 서버 `failure_code` 는 계약된 값이고 한번 나간 값은 바뀌지 않는다. 코드마다 무엇이 잘못됐고
 * 다음에 무엇을 하면 되는지를 이 표 **한 곳**이 소유한다(규칙 §11). `failure_detail` 은 서버
 * 원문이라 이 표가 다루지 않는다 — 화면이 접어 두고 사용자가 펼칠 때만 보인다(규칙 §5).
 *
 * Owns:
 * - 코드 → 문구 · 다음 행동, 모르는 코드의 폴백.
 *
 * Does not own:
 * - 재요청 동작 — 페이지. 상태 색 — `probeStatus.ts`.
 */
export type ProbeFailureView = {
  message: string;
  nextAction: string;
  /** 다시 요청해서 풀릴 수 있는 실패인지. 원본을 고쳐야 하는 실패와 버튼 문구가 갈린다. */
  retryable: boolean;
};

const RETRY = "잠시 후 Probe 를 다시 요청해 주세요.";
const CHECK_SOURCE = "원본 주소와 그 안의 파일 구성을 확인해 주세요.";

const VIEW_BY_CODE: Record<string, ProbeFailureView> = {
  "collect.source_empty": {
    message: "원본 주소에 파일이 없습니다.",
    nextAction: CHECK_SOURCE,
    retryable: false,
  },
  "collect.catalog_missing": {
    message: "LeRobot `meta/` 카탈로그를 찾지 못했습니다.",
    nextAction: CHECK_SOURCE,
    retryable: false,
  },
  "collect.catalog_unreadable": {
    message: "카탈로그를 읽을 수 없습니다.",
    nextAction: CHECK_SOURCE,
    retryable: false,
  },
  "collect.no_episodes": {
    message: "카탈로그에 episode 가 없습니다.",
    nextAction: CHECK_SOURCE,
    retryable: false,
  },
  "collect.missing_episode_file": {
    message: "카탈로그가 가리키는 파일이 원본에 없습니다.",
    nextAction: CHECK_SOURCE,
    retryable: false,
  },
  "collect.sample_too_large": {
    message: "앞 episode 의 크기가 한 번에 옮길 수 있는 한도를 넘습니다.",
    nextAction: "확보할 episode 수를 줄여 다시 요청해 주세요.",
    retryable: true,
  },
  "collect.transfer_failed": {
    message: "샘플을 옮기는 중 오류가 났습니다.",
    nextAction: RETRY,
    retryable: true,
  },
  "collect.lease_expired": {
    message: "수집 Worker 가 응답을 멈춰 작업이 끝났습니다.",
    nextAction: RETRY,
    retryable: true,
  },
};

const UNKNOWN: ProbeFailureView = {
  message: "샘플을 확보하지 못했습니다.",
  nextAction: RETRY,
  retryable: true,
};

export function probeFailureView(failureCode: string | null): ProbeFailureView {
  if (failureCode === null) {
    return UNKNOWN;
  }
  return VIEW_BY_CODE[failureCode] ?? UNKNOWN;
}
